import React, { useState, useCallback, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, RotateCcw, RotateCw, FlipHorizontal, FlipVertical,
  ZoomIn, ZoomOut, RefreshCw, Save, Crop as CropIcon, Maximize2, Minimize2,
} from 'lucide-react';
import { getCroppedImg, generateThumbnail } from '../utils/cropImage';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECTS = [
  { label: 'Livre', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
];

const DISPLAY_MODES = [
  { key: 'cover', label: 'Preencher', hint: 'Ocupa todo o espaço, corta se precisar' },
  { key: 'contain', label: 'Ajustar', hint: 'Mostra a imagem inteira sem cortar' },
  { key: 'fill', label: 'Estender', hint: 'Estica pra ocupar tudo (pode distorcer)' },
];

const ProductImageEditor = ({ imageSrc, onSave, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [aspect, setAspect] = useState(1); // default 1:1 para cards de produto
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [displayMode, setDisplayMode] = useState('cover');
  const [saving, setSaving] = useState(false);

  // Quando o usuário troca para "Ajustar" (contain), automaticamente muda o aspect para "Livre" e
  // reseta o zoom — assim ele vê a imagem inteira no preview e entende que nada será cortado.
  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
    if (mode === 'contain') {
      setAspect(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    } else if (mode === 'cover' && aspect === null) {
      setAspect(1);
    }
  };

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFlip({ horizontal: false, vertical: false });
    setAspect(1);
  };

  const cropperTransform = useMemo(() => {
    // Cropper handles rotation via prop. Mirror via CSS on the cropper wrapper.
    return {
      transform: `scaleX(${flip.horizontal ? -1 : 1}) scaleY(${flip.vertical ? -1 : 1})`,
    };
  }, [flip]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Modo "Ajustar" (contain) = salvar a imagem INTEIRA sem cortar, respeitando rotação/espelhamento.
      // Modo "Preencher"/"Estender" = usam o retângulo de crop escolhido pelo usuário.
      const cropForSave = displayMode === 'contain' ? null : croppedAreaPixels;
      const result = await getCroppedImg(imageSrc, cropForSave, rotation, flip);
      if (!result?.blob) throw new Error('Falha ao gerar imagem');
      const { blob, ext } = result;

      // Upload cropped image — extensão bate com o formato real (png se tinha transparência).
      const formData = new FormData();
      formData.append('file', blob, `product-${Date.now()}.${ext}`);
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const finalUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;

      // Thumbnail (opcional — para listagens)
      let thumbUrl = finalUrl;
      try {
        const thumbBlob = await generateThumbnail(blob);
        const thumbExt = thumbBlob?.type === 'image/png' ? 'png' : 'jpg';
        const thumbForm = new FormData();
        thumbForm.append('file', thumbBlob, `thumb-${Date.now()}.${thumbExt}`);
        const thumbRes = await axios.post(`${API_URL}/upload`, thumbForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        thumbUrl = `${process.env.REACT_APP_BACKEND_URL}${thumbRes.data.url}`;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Thumbnail upload failed, proceeding without it:', e?.message);
      }

      toast.success('Imagem editada com sucesso!');
      onSave({ url: finalUrl, thumbnail_url: thumbUrl, display_mode: displayMode });
    } catch (e) {
      console.error(e);
      toast.error('Erro ao salvar imagem: ' + (e.message || 'desconhecido'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="product-image-editor" className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-2 md:p-6">
      <div className="bg-white w-full max-w-6xl h-[95vh] md:h-[85vh] flex flex-col md:flex-row shadow-2xl overflow-hidden">
        {/* Área do editor (canvas) — no mobile fica FIXA no topo enquanto os controles rolam abaixo,
             assim o usuário vê a imagem responder a cada ajuste de zoom / aspect / rotação. */}
        <div className="flex-1 relative bg-neutral-100 h-[45vh] md:h-auto md:min-h-[300px] shrink-0">
          <div style={cropperTransform} className="absolute inset-0">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              minZoom={0.5}
              maxZoom={3}
              zoomSpeed={0.3}
              showGrid={true}
              objectFit={aspect ? 'contain' : 'contain'}
            />
          </div>

          {/* Header sobre o canvas */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
            <div className="bg-white/90 backdrop-blur px-4 py-2 pointer-events-auto">
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500">Editor de Imagem</p>
              <p className="text-sm text-neutral-900">Casa in Gold · Ajuste conforme desejar</p>
            </div>
            <button type="button"
              onClick={onCancel}
              data-testid="editor-close"
              className="pointer-events-auto bg-white/90 backdrop-blur w-10 h-10 flex items-center justify-center hover:bg-white"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Painel de controles */}
        <div className="flex-1 min-h-0 w-full md:w-80 bg-white border-l border-neutral-200 flex flex-col overflow-y-auto">
          <div className="p-6 space-y-6 flex-1">
            {/* Proporção do recorte */}
            <div>
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
                <CropIcon className="w-3 h-3" strokeWidth={1.5} /> Recorte
              </p>
              <div className="grid grid-cols-3 gap-2">
                {ASPECTS.map((a) => (
                  <button type="button"
                    key={a.label}
                    data-testid={`aspect-${a.label.toLowerCase()}`}
                    onClick={() => setAspect(a.value)}
                    className={`text-xs py-2 border transition-colors ${
                      aspect === a.value
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-900'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Zoom */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500">Zoom</p>
                <span className="text-xs font-mono text-neutral-500">{Math.round(zoom * 100)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button"
                  data-testid="zoom-out"
                  onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
                  className="w-8 h-8 border border-neutral-300 flex items-center justify-center hover:border-neutral-900"
                >
                  <ZoomOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <input
                  data-testid="zoom-slider"
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                />
                <button type="button"
                  data-testid="zoom-in"
                  onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))}
                  className="w-8 h-8 border border-neutral-300 flex items-center justify-center hover:border-neutral-900"
                >
                  <ZoomIn className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Rotação */}
            <div>
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Rotação</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  data-testid="rotate-left"
                  onClick={() => setRotation((r) => (r - 90) % 360)}
                  className="flex items-center justify-center gap-2 py-2 border border-neutral-300 hover:border-neutral-900 text-xs"
                >
                  <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> 90° esq.
                </button>
                <button type="button"
                  data-testid="rotate-right"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="flex items-center justify-center gap-2 py-2 border border-neutral-300 hover:border-neutral-900 text-xs"
                >
                  <RotateCw className="w-4 h-4" strokeWidth={1.5} /> 90° dir.
                </button>
              </div>
            </div>

            {/* Espelhar */}
            <div>
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Espelhar</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  data-testid="flip-horizontal"
                  onClick={() => setFlip((f) => ({ ...f, horizontal: !f.horizontal }))}
                  className={`flex items-center justify-center gap-2 py-2 border text-xs ${
                    flip.horizontal ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 hover:border-neutral-900'
                  }`}
                >
                  <FlipHorizontal className="w-4 h-4" strokeWidth={1.5} /> H
                </button>
                <button type="button"
                  data-testid="flip-vertical"
                  onClick={() => setFlip((f) => ({ ...f, vertical: !f.vertical }))}
                  className={`flex items-center justify-center gap-2 py-2 border text-xs ${
                    flip.vertical ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 hover:border-neutral-900'
                  }`}
                >
                  <FlipVertical className="w-4 h-4" strokeWidth={1.5} /> V
                </button>
              </div>
            </div>

            {/* Modo de Exibição */}
            <div>
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Modo de exibição no site</p>
              <div className="space-y-2">
                {DISPLAY_MODES.map((m) => (
                  <label
                    key={m.key}
                    data-testid={`display-${m.key}`}
                    className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                      displayMode === m.key ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="display-mode"
                      value={m.key}
                      checked={displayMode === m.key}
                      onChange={() => handleDisplayModeChange(m.key)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm text-neutral-900">{m.label}</p>
                      <p className="text-xs text-neutral-500">{m.hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Reset */}
            <button type="button"
              data-testid="editor-reset"
              onClick={reset}
              className="w-full flex items-center justify-center gap-2 py-3 border border-neutral-300 text-xs uppercase tracking-widest hover:bg-neutral-100"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Resetar ajustes
            </button>
          </div>

          {/* Footer com ações */}
          <div className="border-t border-neutral-200 p-4 flex gap-2 bg-neutral-50">
            <button type="button"
              data-testid="editor-cancel"
              onClick={onCancel}
              disabled={saving}
              className="flex-1 py-3 border border-neutral-900 text-neutral-900 text-xs uppercase tracking-widest hover:bg-neutral-100 disabled:opacity-40"
            >
              Cancelar
            </button>
            <button type="button"
              data-testid="editor-save"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 bg-neutral-900 text-white text-xs uppercase tracking-widest hover:bg-black disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductImageEditor;
